import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LeadImportWizard from '../components/leadImport/LeadImportWizard';
import { leadImportService, LeadImportBatch } from '../services/leadImportService';
import { DataListTable, type Column } from '../components/crm/DataListTable';

const LeadImports: React.FC = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<LeadImportBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showWizard, setShowWizard] = useState(false);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await leadImportService.list({ search, status: statusFilter, page: 1 });
      setBatches(data.results || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load lead imports.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const columns = useMemo<Column<LeadImportBatch>[]>(
    () => [
      {
        id: 'batch',
        header: 'Batch',
        mobilePriority: 'primary',
        mobileLabel: 'Batch',
        render: (batch) => <span className="text-sm text-fg">{batch.name}</span>,
      },
      {
        id: 'source',
        header: 'Source',
        mobileLabel: 'Source',
        render: (batch) => <span className="text-sm text-fg-secondary">{batch.source_type}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (batch) => <span className="text-sm text-fg-secondary">{batch.status}</span>,
      },
      {
        id: 'records',
        header: 'Records',
        mobileLabel: 'Records',
        render: (batch) => <span className="text-sm text-fg-secondary">{batch.total_records}</span>,
      },
      {
        id: 'created',
        header: 'Created',
        mobileLabel: 'Created',
        render: (batch) => (
          <span className="text-sm text-fg-secondary">{new Date(batch.created_at).toLocaleString()}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="p-6">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Lead Imports</h1>
          <p className="mt-1 text-sm text-fg-secondary">Review queued import batches before creating active leads.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowWizard(true)}>
          New Import
        </button>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <input
          className="input-field"
          placeholder="Search by batch name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input-field" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending_review">Pending review</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <button className="btn-secondary" onClick={loadBatches}>
          Apply Filters
        </button>
      </div>

      {error && <div className="mb-4 rounded-md border border-danger/30 bg-danger/10 p-3 text-sm text-danger">{error}</div>}

      <DataListTable<LeadImportBatch>
        columns={columns}
        rows={batches}
        getRowKey={(batch) => batch.id}
        onRowClick={(batch) => navigate(`/lead-imports/${batch.id}`)}
        loading={loading}
        emptyContent={
          <div className="rounded-lg border border-border bg-surface p-6 text-sm text-fg-secondary">
            No lead import batches found.
          </div>
        }
      />

      {showWizard && (
        <LeadImportWizard
          onClose={() => setShowWizard(false)}
          onImported={(batchId) => {
            setShowWizard(false);
            navigate(`/lead-imports/${batchId}`);
          }}
        />
      )}
    </div>
  );
};

export default LeadImports;
